import { lazy, Suspense } from 'react';
import { Outlet, Route, Routes } from 'react-router';
// `react-toastify/unstyled`, not the package root: the *styled* entry's `<ToastContainer>` self-
// injects react-toastify's own default CSS as an unlayered `<style>` tag on mount, which always
// wins the cascade over our themed overrides in index.css regardless of import order. `unstyled`
// has the identical API with that self-injection stripped, leaving our own `ReactToastify.css`
// import (main.tsx) + index.css overrides as the only styling source. `lib/events.ts`'s `toast()`
// import has to match this entry point so both talk to the same toast registry.
import { ToastContainer } from 'react-toastify/unstyled';
import { AppShell } from './components/AppShell';
import { RequireAuth } from './components/RequireAuth';
import { AdminRooms } from './routes/AdminRooms';
import { AdminTemplateEditor } from './routes/AdminTemplateEditor';
import { AdminTemplates } from './routes/AdminTemplates';
import { Home } from './routes/Home';
import { Join } from './routes/Join';
import { Login } from './routes/Login';
import { NotFound } from './routes/NotFound';
import { Room } from './routes/Room';

// Lazy: the OBS overlay page is a separate broadcast-graphic bundle (its own realtime hook/store),
// never loaded by a regular Discord-session visitor — no reason to ship it in the main chunk.
const Overlay = lazy(() => import('./routes/Overlay'));

/** Everything except `/login` needs a session. */
function ProtectedLayout() {
  return (
    <RequireAuth>
      <AppShell>
        <Outlet />
      </AppShell>
    </RequireAuth>
  );
}

export default function App() {
  return (
    <>
      <Routes>
        <Route path="/login" element={<Login />} />
        {/* Outside `ProtectedLayout`: authenticated by the room's overlay token in the query
            string, never a Discord session — see `routes/Overlay.tsx`. */}
        <Route
          path="/rooms/:id/overlay"
          element={
            <Suspense fallback={null}>
              <Overlay />
            </Suspense>
          }
        />
        <Route element={<ProtectedLayout />}>
          <Route path="/" element={<Home />} />
          <Route path="/admin/rooms" element={<AdminRooms />} />
          <Route path="/admin/templates" element={<AdminTemplates />} />
          <Route path="/admin/templates/:id" element={<AdminTemplateEditor />} />
          <Route path="/rooms/:id" element={<Room />} />
          <Route path="/join/:token" element={<Join />} />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
      <ToastContainer position="bottom-right" role="status" autoClose={6000} closeOnClick />
    </>
  );
}
